import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api, { fmtCLP, resolveImg } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import BusinessCard from "../components/BusinessCard";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  Search, Key, Shield, ArrowRight, BadgeCheck, MessageSquareLock, TrendingUp,
  Briefcase, Users, CheckCircle2, Star, MapPin, ChevronRight, Sparkles, Eye
} from "lucide-react";

export default function Landing() {
  const [regions, setRegions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [f, setF] = useState({ q: "", region_id: "", category_id: "" });
  const navigate = useNavigate();
  const location = useLocation();

  // Smooth-scroll to anchor (e.g. /#como-funciona) when present.
  // Re-scrolls after images load to compensate for layout shifts caused by lazy-loaded media.
  useEffect(() => {
    const hash = location.hash?.replace("#", "");
    if (!hash) return;

    const scrollToTarget = (behavior = "smooth") => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior, block: "start" });
      return !!el;
    };

    // Try immediately + retry until DOM element exists
    let attempts = 0;
    const initial = setInterval(() => {
      attempts += 1;
      if (scrollToTarget("smooth") || attempts > 10) clearInterval(initial);
    }, 150);

    // Re-snap after typical image load times to fight layout shift
    const snaps = [800, 1800, 3000].map((ms) =>
      setTimeout(() => scrollToTarget("auto"), ms)
    );

    // Final correction when window fully loaded (all images decoded)
    const onLoad = () => scrollToTarget("auto");
    if (document.readyState === "complete") {
      onLoad();
    } else {
      window.addEventListener("load", onLoad, { once: true });
    }

    // Watch images: when each finishes loading, re-snap once
    const imgs = Array.from(document.images || []);
    const imgListener = () => scrollToTarget("auto");
    imgs.forEach((img) => {
      if (!img.complete) img.addEventListener("load", imgListener, { once: true });
    });

    return () => {
      clearInterval(initial);
      snaps.forEach(clearTimeout);
      window.removeEventListener("load", onLoad);
      imgs.forEach((img) => img.removeEventListener("load", imgListener));
    };
  }, [location.pathname, location.hash]);

  useEffect(() => {
    api.get("/regions").then((r) => setRegions(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/businesses?featured=true&limit=6").then((r) => setFeatured(r.data.items));
    api.get("/faqs").then((r) => setFaqs(r.data));
    api.get("/testimonials").then((r) => setTestimonials(r.data));
  }, []);

  const submitSearch = (e) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v && params.set(k, v));
    navigate(`/marketplace?${params.toString()}`);
  };

  return (
    <div className="App">
      <Navbar />

      {/* HERO - buyer-focused */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white to-zinc-50 border-b border-zinc-200">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1776731072112-4efc16f284b5?w=1600&q=60&auto=format&fit=crop')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-white/65" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="overline text-emerald-700 inline-flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> Oportunidades reales en todo Chile
            </span>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900">
              Encuentra tu <br/>
              <span className="text-emerald-600">próximo negocio</span> aquí
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Compra negocios funcionando con flujo activo: cafeterías, gimnasios,
              minimarkets, restaurantes, salones y mucho más. Conoce oportunidades
              validadas antes que nadie y empieza a generar ingresos desde el primer día.
            </p>

            <form onSubmit={submitSearch} className="mt-10 bg-white border border-zinc-200 rounded-xl p-3 shadow-sm grid grid-cols-1 md:grid-cols-12 gap-2">
              <div className="md:col-span-4 flex items-center gap-2 px-3 border-r md:border-zinc-200">
                <Search className="w-4 h-4 text-slate-400" />
                <Input
                  data-testid="hero-search-input"
                  value={f.q}
                  onChange={(e) => setF({ ...f, q: e.target.value })}
                  placeholder="¿Qué negocio buscas?"
                  className="border-0 shadow-none focus-visible:ring-0 px-0"
                />
              </div>
              <div className="md:col-span-3">
                <Select value={f.region_id} onValueChange={(v) => setF({ ...f, region_id: v })}>
                  <SelectTrigger data-testid="hero-region-select" className="border-0 shadow-none focus:ring-0 h-10">
                    <SelectValue placeholder="Región" />
                  </SelectTrigger>
                  <SelectContent>
                    {regions.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3">
                <Select value={f.category_id} onValueChange={(v) => setF({ ...f, category_id: v })}>
                  <SelectTrigger data-testid="hero-category-select" className="border-0 shadow-none focus:ring-0 h-10">
                    <SelectValue placeholder="Rubro" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="md:col-span-2 btn-primary" data-testid="hero-search-btn">
                Buscar
              </Button>
            </form>

            <div className="mt-8 flex flex-wrap items-center gap-6 text-xs text-slate-500">
              <span><strong className="text-slate-900 text-base">120+</strong> oportunidades activas</span>
              <span><strong className="text-slate-900 text-base">16</strong> regiones de Chile</span>
              <span><strong className="text-slate-900 text-base">14</strong> rubros distintos</span>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                {[
                  "https://images.unsplash.com/photo-1648808694138-6706c5efc80a?w=400&q=60&auto=format&fit=crop",
                  "https://images.unsplash.com/photo-1746723378067-83a345ff3160?w=400&q=60&auto=format&fit=crop",
                  "https://images.unsplash.com/photo-1671970922029-0430d2ae122c?w=400&q=60&auto=format&fit=crop",
                  "https://images.unsplash.com/photo-1729394405518-eaf2a0203aa7?w=400&q=60&auto=format&fit=crop",
                ].map((src, idx) => (
                  <Link key={idx} to="/marketplace" className={`${idx % 2 ? "mt-8" : ""} block group`} data-testid={`hero-card-${idx}`}>
                    <div className="aspect-[3/4] rounded-xl overflow-hidden bg-zinc-200 border border-zinc-200">
                      <img
                        src={src}
                        alt=""
                        loading="eager"
                        fetchpriority="high"
                        decoding="async"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ¿Qué es derecho de llave? - buyer focused */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24 grid md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-5">
          <span className="overline text-amber-700">El concepto</span>
          <h2 className="mt-3 font-display text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
            ¿Por qué comprar un negocio en marcha?
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Olvídate de partir desde cero. Aquí encuentras negocios que ya funcionan, con
            clientela, marca, permisos y un equipo listo para seguir operando bajo tu mando.
          </p>
        </div>
        <div className="md:col-span-7 grid sm:grid-cols-3 gap-4">
          <div className="border border-zinc-200 rounded-lg p-5 bg-white">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            <p className="mt-3 font-semibold">Ingresos desde el día 1</p>
            <p className="text-sm text-slate-500 mt-1">Compras un negocio con flujo activo y caja generándose.</p>
          </div>
          <div className="border border-zinc-200 rounded-lg p-5 bg-white">
            <Users className="w-5 h-5 text-emerald-600" />
            <p className="mt-3 font-semibold">Clientela y equipo</p>
            <p className="text-sm text-slate-500 mt-1">Cartera consolidada y personal capacitado incluido.</p>
          </div>
          <div className="border border-zinc-200 rounded-lg p-5 bg-white">
            <Briefcase className="w-5 h-5 text-emerald-600" />
            <p className="mt-3 font-semibold">Permisos vigentes</p>
            <p className="text-sm text-slate-500 mt-1">Patentes y autorizaciones listas para seguir operando.</p>
          </div>
        </div>
      </section>

      {/* Categorías */}
      <section className="bg-white border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="overline text-emerald-700">Explora por rubro</span>
              <h2 className="mt-2 font-display text-3xl lg:text-4xl font-bold tracking-tight">¿Qué tipo de negocio buscas?</h2>
            </div>
            <Link to="/marketplace" className="hidden sm:flex items-center gap-1 text-sm font-semibold hover:underline" data-testid="see-all-categories">
              Ver todas <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 staggered">
            {categories.map((c) => (
              <Link
                key={c.id}
                to={`/marketplace?category_id=${c.id}`}
                data-testid={`category-${c.slug}`}
                className="group bg-zinc-50 hover:bg-slate-900 hover:text-white border border-zinc-200 rounded-lg p-4 text-center transition-colors"
              >
                <div className="aspect-square rounded-md overflow-hidden mb-3 bg-zinc-200">
                  <img src={resolveImg(c.image_url, 300)} alt={c.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </div>
                <p className="text-sm font-semibold">{c.name}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Destacados */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="overline text-emerald-700">Oportunidades destacadas</span>
            <h2 className="mt-2 font-display text-3xl lg:text-4xl font-bold tracking-tight">Negocios funcionando hoy</h2>
            <p className="mt-2 text-sm text-slate-500">Validados por nuestro equipo. Listos para nuevo dueño.</p>
          </div>
          <Button asChild variant="outline" data-testid="see-all-businesses"><Link to="/marketplace">Ver todos</Link></Button>
        </div>
        {featured.length === 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
                <div className="aspect-[4/3] bg-zinc-200 animate-pulse" />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-zinc-200 rounded animate-pulse w-2/3" />
                  <div className="h-3 bg-zinc-100 rounded animate-pulse" />
                  <div className="h-8 bg-zinc-100 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 staggered">
            {featured.map((b) => (<BusinessCard key={b.id} biz={b} />))}
          </div>
        )}
      </section>

      {/* Cómo encontrar y comprar - buyer-only */}
      <section id="como-funciona" className="bg-slate-900 text-white scroll-mt-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <span className="overline text-emerald-400">Cómo funciona para ti</span>
            <h2 className="mt-3 font-display text-3xl lg:text-4xl font-bold tracking-tight">
              Tu próximo negocio en 4 pasos
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed">
              Te acompañamos desde la primera búsqueda hasta el cierre, con un proceso ordenado y profesional.
            </p>
          </div>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Search className="w-5 h-5" />, n: "01", title: "Explora", desc: "Navega oportunidades por región, comuna y rubro. Compara precios y rentabilidad." },
              { icon: <Eye className="w-5 h-5" />, n: "02", title: "Conoce", desc: "Revisa fichas detalladas con métricas operativas, fotos y descripción del negocio." },
              { icon: <MessageSquareLock className="w-5 h-5" />, n: "03", title: "Consulta", desc: "Pide más información a través de la plataforma. Tu identidad queda protegida." },
              { icon: <CheckCircle2 className="w-5 h-5" />, n: "04", title: "Cierra", desc: "Avanza con acompañamiento profesional hasta concretar la compra." },
            ].map((s, i) => (
              <div key={i} className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <div className="w-10 h-10 rounded-full bg-emerald-500 text-slate-900 flex items-center justify-center">
                  {s.icon}
                </div>
                <p className="mt-5 text-xs text-emerald-300 font-semibold tracking-widest">{s.n}</p>
                <p className="mt-1 font-display font-bold text-lg">{s.title}</p>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12">
            <Button asChild className="btn-investment h-12 px-8 text-base" data-testid="cta-explore-marketplace">
              <Link to="/marketplace">Explorar oportunidades <ChevronRight className="w-4 h-4 ml-1" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <span className="overline text-emerald-700">Preguntas frecuentes</span>
        <h2 className="mt-2 font-display text-3xl lg:text-4xl font-bold tracking-tight">Lo que debes saber antes de comprar</h2>
        <Accordion type="single" collapsible className="mt-10">
          {faqs.filter((q) => !/comisi/i.test(q.question)).map((q) => (
            <AccordionItem key={q.id} value={q.id} data-testid={`faq-${q.id}`}>
              <AccordionTrigger className="text-left font-semibold">{q.question}</AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">{q.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA final */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-slate-900 rounded-2xl p-10 lg:p-16 grid md:grid-cols-2 gap-10 items-center relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-80 h-80 bg-amber-500/20 rounded-full blur-3xl"/>
          <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-emerald-500/20 rounded-full blur-3xl"/>
          <div className="relative">
            <h3 className="font-display text-3xl lg:text-4xl font-bold text-white tracking-tight">
              Tu próximo negocio te está esperando
            </h3>
            <p className="mt-4 text-slate-300 max-w-md">
              Cientos de inversionistas como tú están explorando oportunidades hoy. Empieza ahora.
            </p>
          </div>
          <div className="relative flex flex-col sm:flex-row gap-3 justify-end">
            <Button asChild className="btn-investment h-12 px-7" data-testid="cta-final-explore">
              <Link to="/marketplace">Buscar oportunidades</Link>
            </Button>
            <Button asChild variant="outline" className="h-12 px-7 bg-transparent text-white border-white/30 hover:bg-white hover:text-slate-900" data-testid="cta-final-register">
              <Link to="/register">Crear cuenta gratis</Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
